// Tremor Switch [v0.0.1]

import * as SwitchPrimitives from '@radix-ui/react-switch'
import { VariantProps, cva } from 'class-variance-authority'

import { cn } from '../utils'

const rootVariants = cva(
  'group relative isolate inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5 shadow-inner outline-none ring-1 ring-inset transition-all',
  {
    defaultVariants: {
      size: 'default',
    },
    variants: {
      size: {
        default: 'h-5 w-9',
        sm: 'h-4 w-7',
      },
    },
  }
)

const thumbVariants = cva(
  'pointer-events-none relative inline-block transform appearance-none rounded-full border-none shadow-lg outline-none transition-all duration-150 ease-in-out focus:border-none focus:outline-none focus:outline-transparent',
  {
    defaultVariants: {
      size: 'default',
      state: 'enabled',
    },
    variants: {
      size: {
        default: 'h-4 w-4 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
        sm: 'h-3 w-3 data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0',
      },
      state: {
        disabled: 'shadow-none bg-gray-50 dark:bg-gray-500',
        enabled: 'bg-white dark:bg-gray-50',
      },
    },
  }
)

interface ISwitchProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>, 'asChild'>,
    VariantProps<typeof rootVariants> {}

function Switch({ className, disabled, size, ...props }: ISwitchProps) {
  return (
    <SwitchPrimitives.Root
      className={cn(
        rootVariants({ size }),
        'bg-gray-200 dark:bg-gray-950',
        'ring-black/5 dark:ring-gray-800',
        'data-[state=checked]:bg-blue-500 data-[state=checked]:dark:bg-blue-500',
        'data-[disabled]:cursor-default',
        'data-[disabled]:data-[state=checked]:bg-blue-200 data-[disabled]:data-[state=checked]:ring-gray-300 data-[disabled]:data-[state=checked]:dark:bg-blue-900 data-[disabled]:data-[state=checked]:dark:ring-gray-900',
        'data-[disabled]:data-[state=unchecked]:bg-gray-100 data-[disabled]:data-[state=unchecked]:ring-gray-300 data-[disabled]:data-[state=unchecked]:dark:bg-gray-800 data-[disabled]:data-[state=unchecked]:dark:ring-gray-700',
        className
      )}
      disabled={disabled}
      {...props}
    >
      <SwitchPrimitives.Thumb
        className={thumbVariants({
          size,
          state: disabled ? 'disabled' : 'enabled',
        })}
      />
    </SwitchPrimitives.Root>
  )
}

Switch.displayName = 'Switch'

export { Switch }
